import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { ReagentItem } from 'entities/reagent_item'
import { ReactNode, useMemo } from 'react'
import { Badge, OverlayTrigger, Popover, Table } from 'react-bootstrap'
import { Form } from 'utils/hooks'
import { toDateText } from 'utils/util'

export const reagentItemColumnInfoList = (form?: Form<Record<string, unknown>>, showRegentName?: boolean): ColumnInfoList => {
    const values: ColumnInfo[] = [
        { name: 'ID', th: true, width: 50, align: 'center', form: form, orderAttr: 'reagent_item_id', searchType: { name: 'id', type: 'multiple_text' } },
        {
            name: '箱No',
            th: true,
            width: 70,
            align: 'center',
            form: form,
            orderAttr: 'reagent_item_box_number',
            searchType: { name: 'box_number', type: 'number' },
        },
        { name: 'Lot', th: true, width: 100, form: form, orderAttr: 'reagent_item_lot', searchType: { name: 'lot', type: 'text' } },
        { name: 'ステータス', th: true, width: 90, align: 'center' },
        { name: '開封者', th: true, width: 120, align: 'center' },
        {
            name: '納品日',
            th: true,
            width: 100,
            form: form,
            align: 'center',
            orderAttr: 'reagent_item_delivered_date',
            searchType: { name: 'delivered_date', type: 'date' },
        },
        {
            name: '製造年月日',
            th: true,
            width: 100,
            form: form,
            align: 'center',
            orderAttr: 'reagent_item_manufactured_date',
            searchType: { name: 'manufactured_date', type: 'date' },
        },
        {
            name: '使用期限',
            th: true,
            width: 100,
            align: 'center',
            form: form,
            orderAttr: 'reagent_item_expired_date',
            searchType: { name: 'expired_date', type: 'date' },
        },
        {
            name: '開封日',
            th: true,
            width: 100,
            align: 'center',
            form: form,
            orderAttr: 'reagent_item_opened_date',
            searchType: { name: 'opened_date', type: 'date' },
        },
        {
            name: '廃棄日',
            th: true,
            width: 100,
            align: 'center',
            form: form,
            orderAttr: 'reagent_item_used_date',
            searchType: { name: 'used_date', type: 'date' },
        },
        {
            name: '価格(税抜)',
            th: true,
            width: 100,
            form: form,
            orderAttr: 'reagent_item_price',
            searchType: { name: 'price', type: 'number' },
            align: 'right',
        },
        {
            name: '移動平均価格',
            th: true,
            width: 100,
            form: form,
            orderAttr: 'reagent_price',
            searchType: { name: 'moving_average', type: 'number' },
            align: 'right',
        },
        {
            name: '生成元',
            th: true,
            width: 90,
            align: 'center',
        },
        { name: '備考', th: true, minWidth: 300, form: form, orderAttr: 'reagent_note', searchType: { name: 'note', type: 'text' } },
    ]

    if (showRegentName) {
        values.splice(1, 0, { name: '試薬', th: true, width: 120, form: form, orderAttr: 'reagent_item_reagent_id' })
    }
    return {
        tableName: '',
        values: values,
    }
}

export type ReagentItemTableDataProps = {
    reagentItem?: ReagentItem
    displaySetting: DisplaySetting
    showRegentName?: boolean
}

export const ReagentItemTableData = ({ reagentItem, displaySetting, showRegentName }: ReagentItemTableDataProps): JSX.Element => {
    if (!reagentItem) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }

    const createCells = useMemo(() => {
        return (
            <>
                <TableCell align="center">{reagentItem.id}</TableCell>
                {showRegentName && <TableCell>{reagentItem.reagent?.name}</TableCell>}
                <TableCell align="center">{reagentItem.boxNumber}</TableCell>
                <TableCell>{reagentItem.lot}</TableCell>
                <TableCell align="center">
                    {reagentItem.usedDate ? (
                        <Badge bg="danger">廃棄済</Badge>
                    ) : reagentItem.openedDate ? (
                        <Badge bg="primary">開封済</Badge>
                    ) : (
                        <Badge bg="secondary">未開封</Badge>
                    )}
                </TableCell>
                <TableCell align="center">{reagentItem.openedUser?.fullName}</TableCell>
                <TableCell align="center">{toDateText(reagentItem.deliveredDate)}</TableCell>
                <TableCell align="center">{toDateText(reagentItem.manufacturedDate)}</TableCell>
                <TableCell align="center">{toDateText(reagentItem.expiredDate)}</TableCell>
                <TableCell align="center">{toDateText(reagentItem.openedDate)}</TableCell>
                <TableCell align="center">{toDateText(reagentItem.usedDate)}</TableCell>
                <TableCell unit="円" align="right">
                    {reagentItem.price?.toLocaleString()}
                </TableCell>
                <TableCell align="right" unit="円">
                    {reagentItem.movingAverage && Math.round(reagentItem.movingAverage).toLocaleString()}
                </TableCell>

                <TableCell align="center">
                    {reagentItem.sourceReagentItems && reagentItem.sourceReagentItems.length > 0 && (
                        <OverlayTrigger
                            trigger="hover"
                            placement="left"
                            overlay={
                                <Popover id="popover-basic">
                                    <Popover.Header as="h3">生成元の試薬</Popover.Header>
                                    <Popover.Body>
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>試薬</th>
                                                    <th style={{ textAlign: 'center' }}>ID</th>
                                                    <th style={{ minWidth: 150 }}>ロット</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reagentItem.sourceReagentItems?.map((sri) => {
                                                    return (
                                                        <tr key={'source-reagent-id' + sri.id}>
                                                            <td>{sri.reagent?.name}</td>
                                                            <td style={{ textAlign: 'center' }}>{sri.id}</td>
                                                            <td>{sri.lot}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <div className="linked-text">{reagentItem.sourceReagentItems?.length} 件</div>
                        </OverlayTrigger>
                    )}
                </TableCell>
                <TableCell>{reagentItem.note}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
